/**
 * quick-buy-sibling-handler.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Canonical colour-switching handler for quick-buy modals.
 *
 * This is the single source of truth for sibling-switching inside
 * <quick-buy-modal> elements. Previously the same logic lived in two places:
 *   • snippets/product-quick-buy.liquid  (inline <script>, Shop the Look PDP)
 *   • assets/wishlist-quick-buy.js       (account page wishlist modals)
 *
 * Both shared the same __qbSiblingHandlerReady guard so only one copy ever
 * ran, but divergence was a maintenance risk. This file is now loaded on
 * every surface that renders quick-buy modals:
 *   • sections/main-product.liquid       (PDP / Shop the Look)
 *   • sections/main-customers-account.liquid (account wishlist)
 *
 * Dependencies (all optional — guarded with null-checks):
 *   • window.__siblingHelpers  — exported by sibling-ajax.js on product pages.
 *     Provides getSelectedSize() / restoreSelectedSize() for size persistence.
 *     NOT available on the account page; all calls below guard with `&&`.
 *
 * Loading order note:
 *   On the PDP, sibling-ajax.js runs first (it appears earlier in the DOM)
 *   and populates window.__siblingHelpers before any modal interaction.
 *   On the account page, window.__siblingHelpers is simply absent and the
 *   size-persistence feature is silently skipped — correct behaviour.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // Guard: only register once regardless of how many times this file is parsed.
  // wishlist-quick-buy.js checks the same flag — whichever loads first wins.
  if (window.__qbSiblingHandlerReady) return;
  window.__qbSiblingHandlerReady = true;

  // ── Fetch cache — keyed by normalised URL (no query params, no trailing /) ──
  var siblingCache = new Map();

  function normUrl(url) {
    return url.split('?')[0].replace(/\/$/, '');
  }

  function getSectionId() {
    var w = document.querySelector('[data-product-section-id]');
    return w ? w.dataset.productSectionId : null;
  }

  function buildFetchUrl(url) {
    var sectionId = getSectionId();
    var base = normUrl(url);
    return sectionId
      ? base + '?section_id=' + sectionId
      : base;
  }

  /** Returns a Promise<string|null>. Caches by normalised URL. */
  function fetchSiblingHtml(url) {
    var key = normUrl(url);
    if (siblingCache.has(key)) return siblingCache.get(key);
    var promise = fetch(buildFetchUrl(url))
      .then(function (r) { return r.ok ? r.text() : null; })
      .catch(function () { siblingCache.delete(key); return null; });
    siblingCache.set(key, promise);
    return promise;
  }

  // ── Preload on hover — warms cache before the click lands ────────────────
  document.body.addEventListener('pointerenter', function (e) {
    var link = e.target.closest('[data-sibling-link]');
    if (!link || !link.closest('quick-buy-modal')) return;
    var url = link.getAttribute('data-sibling-url') || link.getAttribute('href');
    if (url) fetchSiblingHtml(url);
  }, true);

  // ── Click handler ─────────────────────────────────────────────────────────
  document.body.addEventListener('click', function (e) {
    var link = e.target.closest('[data-sibling-link]');
    if (!link) return;

    var modal = link.closest('quick-buy-modal');
    if (!modal) return;

    e.preventDefault();
    e.stopPropagation();

    var url = link.getAttribute('data-sibling-url') || link.getAttribute('href');
    if (!url) return;

    // Preserve size selection across colour switches (PDP only — helpers may be absent)
    var savedSize = window.__siblingHelpers
      ? window.__siblingHelpers.getSelectedSize(modal)
      : null;

    // Mark clicked swatch as active
    modal.querySelectorAll('[data-sibling-link]').forEach(function (s) {
      s.classList.remove('is-selected');
    });
    link.classList.add('is-selected');

    // Fade out current content
    var oldContent = modal.firstElementChild;
    if (oldContent) {
      oldContent.style.transition = 'opacity 0.12s ease';
      oldContent.style.opacity = '0.4';
    }

    fetchSiblingHtml(url)
      .then(function (html) {
        if (!html) { window.location.href = url; return; }

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var tpl = doc.getElementById('quick-buy-content');
        if (!tpl) return;

        var frag = tpl.content ? tpl.content.cloneNode(true) : tpl.cloneNode(true);
        var newContent = frag.querySelector('product-rerender') || frag.firstElementChild;
        if (!newContent) return;

        // Start invisible so the fade-in is smooth
        newContent.style.opacity = '0';
        newContent.style.transition = 'opacity 0.15s ease';

        modal.removeAttribute('data-size-confirmed');
        modal.replaceChildren(newContent);

        if (savedSize && window.__siblingHelpers) {
          window.__siblingHelpers.restoreSelectedSize(newContent, savedSize);
        }

        // rAF ensures opacity:0 is painted before we flip to 1
        requestAnimationFrame(function () {
          newContent.style.opacity = '1';
        });

        modal.setAttribute('product-url', url);
      })
      .catch(function () {
        window.location.href = url;
      });
  }, true);

}());
